/**
 * Members a send refused because a merge field could not be filled
 * (`failed[]` entries with `reason: "unfilled_merge_fields"`). For each member:
 * which variable, and why. Nothing was sent or recorded for them.
 */

import React from 'react';
import { Ban } from 'lucide-react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import type { UnfilledSendFailure } from './api';
import { UnresolvedList } from './TemplateCheck';

interface UnfilledMergeFieldsProps {
  failures: UnfilledSendFailure[];
  /** member_id → display name (never needed for the message itself). */
  memberNames?: Record<number, string>;
}

export function UnfilledMergeFields({ failures, memberNames }: UnfilledMergeFieldsProps) {
  const { t } = useAppTranslation();
  if (!failures || failures.length === 0) return null;
  return (
    <div role="alert" className="rounded-md border border-danger bg-danger-soft px-3 py-2" data-testid="unfilled-merge-fields">
      <p className="flex items-center gap-1.5 text-sm font-semibold text-danger">
        <Ban className="h-4 w-4" aria-hidden />
        {t('templateVariables.blocked.title', 'Not sent: {{count}} member(s) have merge fields that could not be filled', {
          count: failures.length,
        })}
      </p>
      <ul className="mt-2 space-y-2">
        {failures.map((f) => (
          <li key={f.member_id} className="text-sm">
            <span className="font-medium text-ink">
              {memberNames?.[f.member_id] ?? t('templateVariables.blocked.member', 'Member #{{id}}', { id: f.member_id })}
            </span>
            {f.unresolved && f.unresolved.length > 0 ? (
              <UnresolvedList items={f.unresolved} tone="error" />
            ) : (
              <p className="text-xs text-ink-700">{f.error}</p>
            )}
          </li>
        ))}
      </ul>
      <p className="mt-2 text-xs text-ink-700">
        {t('templateVariables.blocked.hint', 'Fill in the missing values or edit the message, then send again.')}
      </p>
    </div>
  );
}
